import React, { useState, useRef, useEffect, useCallback, type ForwardedRef } from "react";
import { FaInfoCircle } from "react-icons/fa";
import clsx from "clsx";

/**
 * Defina uma constante para o margin no topo do arquivo (após imports)
 */
const TOOLTIP_MARGIN = 16; // margem extra para garantir que o tooltip não vaze

/**
 * Hook para autoajuste de posição do tooltip.
 * Tenta manter o tooltip visível na viewport, mudando o placement se necessário.
 */
function useAutoPlacement(
  open: boolean,
  triggerRef: React.RefObject<HTMLSpanElement | null>,
  bubbleRef: React.RefObject<HTMLSpanElement | null>,
  initialPlacement: "top" | "bottom" | "left" | "right"
) {
  const [placement, setPlacement] = useState<"top" | "bottom" | "left" | "right">(initialPlacement);

  useEffect(() => {
    if (!open || !triggerRef.current || !bubbleRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const bubbleRect = bubbleRef.current.getBoundingClientRect();
    const margin = TOOLTIP_MARGIN; // use a constante

    // Checa se cabe em cada direção
    const fits = {
      top: triggerRect.top >= bubbleRect.height + margin,
      bottom: window.innerHeight - triggerRect.bottom >= bubbleRect.height + margin,
      left: triggerRect.left >= bubbleRect.width + margin,
      right: window.innerWidth - triggerRect.right >= bubbleRect.width + margin,
    };

    // Mantém o placement atual se couber, senão tenta outras direções em ordem de prioridade
    if (fits[placement]) return;

    const priorities: ("top" | "bottom" | "right" | "left")[] =
      placement === "top"
        ? ["bottom", "right", "left", "top"]
        : placement === "bottom"
        ? ["top", "right", "left", "bottom"]
        : placement === "left"
        ? ["right", "top", "bottom", "left"]
        : ["left", "top", "bottom", "right"];

    for (const dir of priorities) {
      if (fits[dir]) {
        setPlacement(dir);
        return;
      }
    }
    // Se não couber em nenhum, mantém o original
    setPlacement(initialPlacement);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPlacement, triggerRef, bubbleRef]);

  // Atualiza placement se initialPlacement mudar
  useEffect(() => {
    setPlacement(initialPlacement);
  }, [initialPlacement]);

  return placement;
}

/**
 * Tooltip
 *
 * Componente acessível de tooltip para React, suportando conteúdo HTML/JSX, delays configuráveis, forwardRef para testes/manipulação externa,
 * propagação de props extras, e fechamento automático ao scroll ou ESC.
 *
 * - O tooltip pode ser aberto por foco, mouse ou teclado.
 * - O foco automático no bubble só ocorre se aberto por teclado.
 * - O tooltip fecha ao pressionar ESC, ao dar scroll ou ao perder foco (exceto se o foco for para o próprio bubble).
 * - O conteúdo pode ser texto, HTML ou JSX.
 * - O componente aceita ref externo (forwardRef) e props extras são propagadas para o trigger.
 *
 * @component
 * @example
 * // Tooltip simples com texto
 * <Tooltip tooltip="Informação importante">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Tooltip com HTML/JSX e delay
 * <Tooltip
 *   tooltip={<span><b>Importante:</b> Leia com atenção!</span>}
 *   openDelay={300}
 *   closeDelay={200}
 *   placement="right"
 * >
 *   <span>ℹ️</span>
 * </Tooltip>
 *
 * @example
 * // Tooltip padrão com ícone
 * <Tooltip tooltip="Dica rápida" />
 *
 * @param {Object} props
 * @param {React.ReactNode} props.tooltip Conteúdo do tooltip (aceita texto, HTML ou JSX).
 * @param {React.ReactNode} [props.children] Elemento que dispara o tooltip. Se não fornecido, exibe um ícone de informação.
 * @param {"top"|"bottom"|"left"|"right"} [props.placement="top"] Posição do tooltip em relação ao trigger.
 * @param {string} [props.className] Classe CSS adicional para o trigger.
 * @param {number} [props.tabIndex=0] tabIndex do trigger.
 * @param {string} [props.data-testid] Test id para testes automatizados.
 * @param {"polite"|"assertive"|"off"} [props.ariaLive="off"] Valor do atributo aria-live para acessibilidade.
 * **Atenção:** Use `"polite"` apenas para tooltips com feedback dinâmico relevante para o usuário. Para tooltips meramente informativos, mantenha `"off"` (padrão).
 * @param {number} [props.openDelay=0] Delay em ms para abrir o tooltip ao focar/passar mouse.
 * @param {number} [props.closeDelay=0] Delay em ms para fechar o tooltip ao desfocar/sair do mouse.
 * @param {React.Ref<HTMLSpanElement>} [ref] Ref encaminhado para o trigger do tooltip.
 * @param {Object} [rest] Outras props são propagadas para o trigger <span>.
 *
 * @returns {JSX.Element} Tooltip renderizado.
 */
export interface TooltipProps extends React.HTMLAttributes<HTMLSpanElement> {
  tooltip: React.ReactNode;
  children?: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  tabIndex?: number;
  "data-testid"?: string;
  ariaLive?: "polite" | "assertive" | "off";
  openDelay?: number;
  closeDelay?: number;
  autoPlacement?: boolean;
  anchorId?: string;
  disabled?: boolean;
  width?: number | string;
  iconColor?: string;
  iconSize?: number | string;
  triggerless?: boolean; // <-- ADICIONE ESTA LINHA
}

const Tooltip = React.forwardRef<HTMLSpanElement, TooltipProps>(function Tooltip(
  {
    tooltip,
    children,
    placement = "top",
    className,
    tabIndex = 0,
    "data-testid": dataTestId,
    ariaLive = "off",
    openDelay = 0,
    closeDelay = 0,
    autoPlacement: autoPlacementProp = true,
    anchorId,
    disabled,
    width,
    iconColor,
    iconSize,
    triggerless, // <-- ADICIONE ESTA LINHA
    ...rest
  },
  ref: ForwardedRef<HTMLSpanElement>
) {
  const [open, setOpen] = useState(false);
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false);
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).slice(2, 10)}`).current;
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null) as React.RefObject<HTMLSpanElement>;
  const openTimeout = useRef<NodeJS.Timeout | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Hook de autoajuste de posição
  const computedPlacement = useAutoPlacement(open, triggerRef, bubbleRef, placement);

  // NOVO: Ajuste para manter o bubble sempre visível na viewport
  const [bubbleStyle, setBubbleStyle] = useState<React.CSSProperties>({});
  const [arrowOffset, setArrowOffset] = useState<{ left?: number; top?: number }>({});

  useEffect(() => {
    if (!open || !bubbleRef.current) {
      setBubbleStyle({});
      setArrowOffset({});
      return;
    }

    const bubble = bubbleRef.current;
    const rect = bubble.getBoundingClientRect();
    const margin = TOOLTIP_MARGIN; // use a constante

    const style: React.CSSProperties = {};
    const arrow: { left?: number; top?: number } = {};

    // Ajuste horizontal
    if (rect.left < margin) {
      const offset = margin - rect.left;
      style.left = offset;
      style.transform = (style.transform ?? "") + " translateX(0)";
      arrow.left = Math.max(16, rect.width / 2 - offset); // seta mais para a direita
    } else if (rect.right > window.innerWidth - margin) {
      const offset = window.innerWidth - margin - rect.right;
      style.left = offset;
      style.transform = (style.transform ?? "") + " translateX(0)";
      arrow.left = Math.min(rect.width - 16, rect.width / 2 - offset); // seta mais para a esquerda
    }

    // Ajuste vertical
    if (rect.top < margin) {
      const offset = margin - rect.top;
      style.top = offset;
      style.transform = (style.transform ?? "") + " translateY(0)";
      arrow.top = Math.max(16, rect.height / 2 - offset); // seta mais para baixo
    } else if (rect.bottom > window.innerHeight - margin) {
      const offset = window.innerHeight - margin - rect.bottom;
      style.top = offset;
      style.transform = (style.transform ?? "") + " translateY(0)";
      arrow.top = Math.min(rect.height - 16, rect.height / 2 - offset); // seta mais para cima
    }

    setBubbleStyle(style);
    setArrowOffset(arrow);
  }, [open, computedPlacement, placement]);

  // Fecha tooltip ao pressionar ESC ou ao dar scroll
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "Escape" || e.key === "Esc") && open) setOpen(false);
    };
    const handleScroll = () => {
      if (open) setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  // Move foco para o tooltip bubble se aberto por teclado
  useEffect(() => {
    if (open && openedByKeyboard && bubbleRef.current) {
      bubbleRef.current.focus();
    }
  }, [open, openedByKeyboard]);

  // Limpa timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (openTimeout.current) clearTimeout(openTimeout.current);
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, []);

  // Funções para abrir/fechar com delay
  const handleOpen = useCallback(
    (byKeyboard = false) => {
      if (disabled) return; // <-- Adicione esta linha
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
      if (openDelay > 0) {
        openTimeout.current = setTimeout(() => {
          setOpen(true);
          setOpenedByKeyboard(byKeyboard);
        }, openDelay);
      } else {
        setOpen(true);
        setOpenedByKeyboard(byKeyboard);
      }
    },
    [openDelay, disabled]
  );

  const handleClose = useCallback(() => {
    if (openTimeout.current) clearTimeout(openTimeout.current);
    if (closeDelay > 0) {
      closeTimeout.current = setTimeout(() => setOpen(false), closeDelay);
    } else {
      setOpen(false);
    }
    setOpenedByKeyboard(false);
  }, [closeDelay]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        setOpen(o => {
          const next = !o;
          setOpenedByKeyboard(next);
          return next;
        });
      }
    },
    []
  );

  const handleFocus = useCallback(() => {
    handleOpen(true);
  }, [handleOpen]);

  const handleMouseEnter = useCallback(() => {
    handleOpen(false);
  }, [handleOpen]);

  // Função para fechar, mas só se o foco não for para o bubble
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (bubbleRef.current && e.relatedTarget === bubbleRef.current) {
      return;
    }
    handleClose();
  }, [handleClose]);

  // Adicione este efeito para garantir que o tooltip nunca fique aberto se disabled for true
  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
      setOpenedByKeyboard(false);
    }
  }, [disabled, open]);

  // PATCH: modo triggerless (apenas bubble, sem trigger, sem ícone)
  if (triggerless) {
    // PATCH: Garante nome acessível mesmo se tooltip estiver vazio/invisível
    const tooltipText =
      typeof tooltip === "string"
        ? tooltip
        : (React.isValidElement(tooltip) &&
            tooltip.props &&
            typeof (tooltip.props as { children?: unknown }).children === "string")
        ? (tooltip.props as { children?: unknown }).children as string
        : undefined;
    const ariaLabel =
      tooltipText && tooltipText.trim().length > 0
        ? tooltipText
        : "Tooltip"; // fallback acessível

    return (
      <span
        ref={bubbleRef}
        className={clsx(
          "absolute z-50 min-w-[140px] max-w-xs px-4 py-3 rounded-xl text-base font-medium leading-snug shadow-xl border transition-all duration-200 select-none",
          "bg-white/90 dark:bg-zinc-900/95 border-violet-200 dark:border-violet-700 text-zinc-900 dark:text-zinc-100",
          { "pointer-events-auto opacity-100 visible": !disabled, "pointer-events-none opacity-0 invisible": disabled },
          className
        )}
        id={anchorId ? anchorId + "-tooltip" : undefined}
        role="tooltip"
        tabIndex={-1}
        aria-live={ariaLive}
        aria-label={ariaLabel} // <-- PATCH: sempre fornece nome acessível
        style={{
          boxShadow:
            "0 4px 24px 0 rgba(25, 118, 210, 0.10), 0 1.5px 6px 0 rgba(98,0,238,0.08), 0 0.5px 1.5px 0 rgba(0,0,0,0.10)",
          ...bubbleStyle,
          ...(width
            ? {
                width: typeof width === "number" ? `${width}px` : width,
                minWidth: 0,
                maxWidth: "none",
              }
            : {}),
        }}
        data-testid={dataTestId}
        {...rest}
      >
        {tooltip}
      </span>
    );
  }

  // Se anchorId for passado, não renderize o trigger, apenas o bubble posicionado fora
  if (anchorId) {
    return (
      <span
        ref={bubbleRef}
        className={clsx(
          "absolute z-50 min-w-[140px] max-w-xs px-4 py-3 rounded-xl text-base font-medium leading-snug shadow-xl border transition-all duration-200 select-none pointer-events-none opacity-0 invisible",
          "bg-white/90 dark:bg-zinc-900/95 border-violet-200 dark:border-violet-700 text-zinc-900 dark:text-zinc-100",
          {
            "opacity-100 visible pointer-events-auto": open && !disabled, // <-- PATCH
            "bottom-[125%] left-1/2 -translate-x-1/2 mb-3": (autoPlacementProp ? computedPlacement : placement) === "top",
            "top-[125%] left-1/2 -translate-x-1/2 mt-3": (autoPlacementProp ? computedPlacement : placement) === "bottom",
            "right-[125%] top-1/2 -translate-y-1/2 mr-3": (autoPlacementProp ? computedPlacement : placement) === "left",
            "left-[125%] top-1/2 -translate-y-1/2 ml-3": (autoPlacementProp ? computedPlacement : placement) === "right",
          },
          className
        )}
        id={anchorId + "-tooltip"}
        role="tooltip"
        tabIndex={-1}
        aria-live={ariaLive}
        style={{
          boxShadow:
            "0 4px 24px 0 rgba(25, 118, 210, 0.10), 0 1.5px 6px 0 rgba(98,0,238,0.08), 0 0.5px 1.5px 0 rgba(0,0,0,0.10)",
          ...bubbleStyle,
        }}
        data-testid={dataTestId} // <-- Add this line
        {...rest}
      >
        {tooltip}
        {/* Seta do tooltip */}
        <span
          className={clsx(
            "absolute w-4 h-4 bg-inherit border-inherit rounded-sm rotate-45 z-[-1]",
            {
              "left-1/2 -translate-x-1/2 bottom-[-8px] border-b border-r border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "top",
              "left-1/2 -translate-x-1/2 top-[-8px] border-t border-l border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "bottom",
              "top-1/2 -translate-y-1/2 right-[-8px] border-t border-r border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "left",
              "top-1/2 -translate-y-1/2 left-[-8px] border-b border-l border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "right",
              "hidden": !open,
            }
          )}
          style={
            (autoPlacementProp ? computedPlacement : placement) === "top" && arrowOffset.left !== undefined
              ? { left: arrowOffset.left, transform: "translateX(0)", bottom: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "bottom" && arrowOffset.left !== undefined
              ? { left: arrowOffset.left, transform: "translateX(0)", top: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "left" && arrowOffset.top !== undefined
              ? { top: arrowOffset.top, transform: "translateY(0)", right: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "right" && arrowOffset.top !== undefined
              ? { top: arrowOffset.top, transform: "translateY(0)", left: -8 }
              : undefined
          }
        />
      </span>
    );
  }

  return (
    <span
      ref={node => {
        triggerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLSpanElement | null>).current = node;
      }}
      className={clsx(
        "inline-flex items-center cursor-pointer relative outline-none font-sans",
        className
      )}
      tabIndex={tabIndex}
      aria-describedby={tooltipId}
      aria-haspopup="true"
      // PATCH: Só adiciona aria-controls quando o tooltip está aberto
      {...(open ? { "aria-controls": tooltipId } : {})}
      data-testid={dataTestId}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleClose}
      onKeyDown={handleKeyDown}
      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      {...rest}
    >
      {children ?? (
        <FaInfoCircle
          aria-hidden="true"
          focusable="false"
          className="text-blue-600 dark:text-blue-400 text-[1.15em] mx-1 drop-shadow-sm"
          style={{
            color: iconColor || undefined,
            fontSize: iconSize
              ? typeof iconSize === "number"
                ? `${iconSize}px`
                : iconSize
              : undefined,
          }}
        />
      )}
      <span
        ref={bubbleRef}
        className={clsx(
          "absolute z-50 min-w-[140px] max-w-xs px-4 py-3 rounded-xl text-base font-medium leading-snug shadow-xl border transition-all duration-200 select-none pointer-events-none opacity-0 invisible",
          "bg-white/90 dark:bg-zinc-900/95 border-violet-200 dark:border-violet-700 text-zinc-900 dark:text-zinc-100",
          {
            "opacity-100 visible pointer-events-auto": open && !disabled,
            "bottom-[125%] left-1/2 -translate-x-1/2 mb-3": (autoPlacementProp ? computedPlacement : placement) === "top",
            "top-[125%] left-1/2 -translate-x-1/2 mt-3": (autoPlacementProp ? computedPlacement : placement) === "bottom",
            "right-[125%] top-1/2 -translate-y-1/2 mr-3": (autoPlacementProp ? computedPlacement : placement) === "left",
            "left-[125%] top-1/2 -translate-y-1/2 ml-3": (autoPlacementProp ? computedPlacement : placement) === "right",
          }
        )}
        id={tooltipId}
        role="tooltip"
        tabIndex={-1}
        aria-live={ariaLive}
        style={{
          boxShadow:
            "0 4px 24px 0 rgba(25, 118, 210, 0.10), 0 1.5px 6px 0 rgba(98,0,238,0.08), 0 0.5px 1.5px 0 rgba(0,0,0,0.10)",
          ...bubbleStyle,
          ...(width
            ? {
                width:
                  typeof width === "number"
                    ? `${width}px`
                    : width,
                minWidth: 0,
                maxWidth: "none",
              }
            : {}),
        }}
      >
        {tooltip}
        {/* Seta do tooltip */}
        <span
          className={clsx(
            "absolute w-4 h-4 bg-inherit border-inherit rounded-sm rotate-45 z-[-1]",
            {
              "left-1/2 -translate-x-1/2 bottom-[-8px] border-b border-r border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "top",
              "left-1/2 -translate-x-1/2 top-[-8px] border-t border-l border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "bottom",
              "top-1/2 -translate-y-1/2 right-[-8px] border-t border-r border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "left",
              "top-1/2 -translate-y-1/2 left-[-8px] border-b border-l border-violet-200 dark:border-violet-700": (autoPlacementProp ? computedPlacement : placement) === "right",
              "hidden": !open,
            }
          )}
          style={
            (autoPlacementProp ? computedPlacement : placement) === "top" && arrowOffset.left !== undefined
              ? { left: arrowOffset.left, transform: "translateX(0)", bottom: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "bottom" && arrowOffset.left !== undefined
              ? { left: arrowOffset.left, transform: "translateX(0)", top: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "left" && arrowOffset.top !== undefined
              ? { top: arrowOffset.top, transform: "translateY(0)", right: -8 }
              : (autoPlacementProp ? computedPlacement : placement) === "right" && arrowOffset.top !== undefined
              ? { top: arrowOffset.top, transform: "translateY(0)", left: -8 }
              : undefined
          }
        />
      </span>
    </span>
  );
});

export default Tooltip;

